/**
 * What a New arrival screen starts with when it is opened against a Gate Pass In waiting in the form's
 * Pending list (`?gatePass=`): the pass's transport in the arrival's own keys, its number for the
 * read-only header row, and the id the create sends so the server can check and link the two.
 *
 * Read through the arrival API, not the gate's: whoever raises the arrival need not hold `gate.view`,
 * and a pass that has since been used or cancelled reads as missing rather than prefilling a create
 * the server would refuse.
 */
import { arrivalTransportFromGatePass } from "@nexseed/shared";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { arrivalCalls } from "@/features/arrival/api";

/** `?gatePass=` as a route's `validateSearch` reads it: a positive integer, or nothing. */
export const gatePassSearch = (
  search: Record<string, unknown>
): { gatePass?: number } => {
  const n = Number(search.gatePass);
  return Number.isInteger(n) && n > 0 ? { gatePass: n } : {};
};

export function useGatePassPrefill(gatePass: number | undefined) {
  const pass = useQuery({
    queryKey: ["arrivals", "gate-pass", gatePass],
    enabled: gatePass !== undefined,
    queryFn: () => arrivalCalls.waitingGatePass(gatePass!),
    retry: false,
  });
  // Memoised so the screen can tell "prefilled and untouched" from "edited" by identity.
  const initialHeader = useMemo(
    () =>
      pass.data
        ? {
            gatePassInId: pass.data.id,
            transport: {
              ...arrivalTransportFromGatePass(pass.data),
              // Shown read-only on the header; the server stamps the real value on create.
              gatePassIn: pass.data.passNo,
            },
          }
        : undefined,
    [pass.data]
  );
  return {
    loading: gatePass !== undefined && pass.isPending,
    missing: gatePass !== undefined && !pass.isPending && !pass.data,
    passId: pass.data?.id,
    initialHeader,
  };
}
